import React, { useState, useEffect } from "react";
import { useNavigate, useLocation, useParams } from "react-router-dom";
import { toast } from "react-toastify";
import "../../style/ToastNotifications.css";
import { FaCheckCircle, FaBox } from "react-icons/fa";
import {
  FiUser,
  FiMapPin,
  FiCalendar,
  FiTruck,
  FiPackage,
  FiSearch,
  FiPhone,
  FiMail,
  FiCheck,
  FiChevronDown,
  FiChevronUp,
  FiX,
  FiFileText,
  FiHash,
  FiMessageSquare,
  FiCamera,
  FiMap,
} from "react-icons/fi";
import "../../style/DeliveredChallan.css";
import api from "../../api/axiosInstance";
import { TokenManager } from "../../utils/tokenManager";
import InsertProduct from "../../components/InsertProduct";
import ProductRow from "../../components/ProductRow";
import ChallanProductsSection from "../../components/ChallanProductsSection";
import { searchCustomers } from "../../api/customerApi";
import { getAllGoods } from "../../api/goodsApi";
import { getSrNos } from "../../api/cylinderMasterApi";
import { validateChallanItemSrCounts } from "../../utils/challanSrValidation";
import { getErrorMessage } from "../../utils/errorHandler";
import ChallanInvoicePopup from "../Invoices/ChallanInvoicePopup";
import { settingsApi } from "../../api/settingsApi";


// Move ProductTable outside component to prevent re-mount on every render
const ProductTable = React.memo(({ prefix, pageType, products, allProducts, onUpdate, onRemove, customerInventory, inventoryLoading, goods, resetVersion }) => {
  // Add state for product search dropdowns
  const [productSearchTerms, setProductSearchTerms] = useState({});
  const [showProductDropdowns, setShowProductDropdowns] = useState({});
  const [filteredGoods, setFilteredGoods] = useState({});

  // Reset internal state when resetVersion changes
  useEffect(() => {
    setProductSearchTerms({});
    setShowProductDropdowns({});
    if (goods && goods.length > 0) {
      const initialFiltered = {};
      products.forEach((_, index) => {
        initialFiltered[index] = goods;
      });
      setFilteredGoods(initialFiltered);
    }
  }, [resetVersion, goods, products]);

  const handleProductSearch = (index, searchTerm) => {
    setProductSearchTerms(prev => ({ ...prev, [index]: searchTerm }));

    // Use customer inventory first, then fall back to general goods
    const searchSource = customerInventory.length > 0 ? customerInventory : goods;
    const filtered = searchSource.filter(item =>
      item.name.toLowerCase().includes(searchTerm.toLowerCase())
    );
    setFilteredGoods(prev => ({ ...prev, [index]: filtered }));
    setShowProductDropdowns(prev => ({ ...prev, [index]: true }));
  };

  const handleProductSelect = async (index, selectedGood) => {
    onUpdate(index, 'name', selectedGood.name);
    if (selectedGood && selectedGood.type) {
      onUpdate(index, 'type', selectedGood.type);
    }
    setProductSearchTerms(prev => ({ ...prev, [index]: selectedGood.name }));
    setShowProductDropdowns(prev => ({ ...prev, [index]: false }));
    const searchSource = customerInventory.length > 0 ? customerInventory : goods;
    setFilteredGoods(prev => ({ ...prev, [index]: searchSource }));
    // Reset previous selections
    onUpdate(index, 'cylinderData', []);
    onUpdate(index, 'selectedSrNos', []);
    onUpdate(index, 'selectedBatch', '');
    onUpdate(index, 'deliveredQty', '');

    try {
      const response = await getSrNos(selectedGood.name, "IN_STOCK");
      const cylinders = response.data || [];
      onUpdate(index, 'cylinderData', cylinders);
    } catch (error) {
      console.error("Error fetching cylinder data:", error);
      toast.error("Failed to fetch cylinder data");
    }
  };

  return (
    <>
      {products.map((product, index) => (
        <ProductRow
          key={product.id}
          mode="delivered"
          product={product}
          index={index}
          productsCount={products.length}
          productSearchTerm={productSearchTerms[index]}
          filteredGoods={filteredGoods[index]}
          goods={customerInventory.length > 0 ? customerInventory : goods}
          productDropdownOpen={showProductDropdowns[index]}
          onProductSearch={handleProductSearch}
          onProductDropdownOpen={(rowIndex, open) => setShowProductDropdowns(prev => ({ ...prev, [rowIndex]: open }))}
          onProductSelect={handleProductSelect}
          onUpdate={onUpdate}
          onRemove={onRemove}
        />
      ))}
    </>
  );
});

function DeliveredChallan() {
  const navigate = useNavigate();
  const location = useLocation();
  const { id } = useParams();

  const [showExtendedProductForm, setShowExtendedProductForm] = useState(false);
  const extendedFormRef = React.useRef(null);

  // State for collapsible sections - using tab-like selection
  const [activeSection, setActiveSection] = useState(null); // 'transport', 'delivery', 'deposit', or null
  const [showDepositDropdown, setShowDepositDropdown] = useState(false);

  const [form, setForm] = useState({
    challanNumber: "",
    challanSequence: "",
    customer: "",
    customerId: "",
    challanType: "Delivered Challan",
    date: new Date().toISOString().split("T")[0],
    siteLocation: "",
    vehicleNo: "",
    transporter: "",
    driverDetails: "",
    driverNumber: "",
    deliveryDetails: "",
    poNo: "",
    depositType: "Deposit",
    depositAmount: "",
    depositNarration: "",
    products: [],
    images: [],
    geoLocation: null,
  });

  // State for tracking field errors
  const [fieldErrors, setFieldErrors] = useState({
    customer: false,
    siteLocation: false,
    products: false,
  });

  const [showInvoicePopup, setShowInvoicePopup] = useState(false);

  // Auto-increment tracking
  const [lastSavedSequence, setLastSavedSequence] = useState(null);

  // Settings state
  const [settings, setSettings] = useState({
    enableChallanImageUpload: false,
    enableChallanGeoLocation: false,
    showInvoicePopupAfterChallan: true,
    useManualChallan: false,
    invoicePrefix: '',
    challanNumberFormat: '',
    challanSequence: '',
    manualChallanStart: '',
  });

  // Load settings on mount
  useEffect(() => {
    const loadSettings = async () => {
      try {
        const res = await settingsApi.getSettings();
        if (res.status === 204) return;
        const data = res.data;
        setSettings({
          enableChallanImageUpload: data.enableChallanImageUpload || false,
          enableChallanGeoLocation: data.enableChallanGeoLocation || false,
          showInvoicePopupAfterChallan: data.showInvoicePopupAfterChallan !== false,
          useManualChallan: data.useManualChallan || false,
          invoicePrefix: data.invoicePrefix || '',
          challanNumberFormat: data.challanNumberFormat || '',
          challanSequence: data.challanSequence || '',
          manualChallanStart: data.manualChallanStart || '',
        });
      } catch (error) {
        console.error("Failed to load settings:", error);
      }
    };
    loadSettings();
  }, []);

  // Customer search state
  const [customers, setCustomers] = useState([]);
  const [filteredCustomers, setFilteredCustomers] = useState([]);
  const [customerSearchTerm, setCustomerSearchTerm] = useState("");
  const [showCustomerDropdown, setShowCustomerDropdown] = useState(false);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest('.deposit-dropdown')) {
        setShowDepositDropdown(false);
      }
      if (!event.target.closest('.customer-search-container')) {
        setShowCustomerDropdown(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Goods state for quick add product
  const [goods, setGoods] = useState([]);
  const [allProducts] = useState([]);
  const [customerInventory, setCustomerInventory] = useState([]);
  const [inventoryLoading, setInventoryLoading] = useState(false);

  // Additional state for new product management
  const [products, setProducts] = useState([{ id: Date.now(), name: "", type: "", deliveredQty: "", receivedQty: "", srNo: [], selectedSrNos: [], cylinderData: [], selectedBatch: "", selectedSrno: "" }]);
  const [pageType] = useState("delivered");
  const [saving, setSaving] = useState(false);

  // Image upload state
  const [imagePreviews, setImagePreviews] = useState([]);

  // Geo location state
  const [geoLocation, setGeoLocation] = useState(null);
  const [fetchingLocation, setFetchingLocation] = useState(false);

  // Handle image upload
  const handleImageUpload = (e) => {
    const files = Array.from(e.target.files);
    if (!files.length) return;

    const validFiles = files.filter(file => file.type.startsWith('image/'));
    if (validFiles.length !== files.length) {
      toast.error('Please upload only image files');
      return;
    }

    validFiles.forEach(file => {
      const reader = new FileReader();
      reader.onloadend = () => {
        const base64 = reader.result;
        setImagePreviews(prev => [...prev, base64]);
        setForm(prev => ({
          ...prev,
          images: [...prev.images, base64]
        }));
      };
      reader.readAsDataURL(file);
    });
  };

  // Remove image
  const handleRemoveImage = (index) => {
    setImagePreviews(prev => prev.filter((_, i) => i !== index));
    setForm(prev => ({
      ...prev,
      images: prev.images.filter((_, i) => i !== index)
    }));
  };

  // Fetch geo location
  const handleFetchGeoLocation = () => {
    if (!navigator.geolocation) {
      toast.error('Geolocation is not supported by your browser');
      return;
    }

    setFetchingLocation(true);
    navigator.geolocation.getCurrentPosition(
      (position) => {
        const location = {
          latitude: position.coords.latitude,
          longitude: position.coords.longitude,
          accuracy: position.coords.accuracy
        };
        setGeoLocation(location);
        setForm(prev => ({
          ...prev,
          geoLocation: location
        }));
        setFetchingLocation(false);
        toast.success('Location captured successfully');
      },
      (error) => {
        console.error('Error getting location:', error);
        toast.error('Failed to get location. Please enable location services.');
        setFetchingLocation(false);
      },
      {
        enableHighAccuracy: true,
        timeout: 10000,
        maximumAge: 0
      }
    );
  };
  const displayDepositAmount =
    form.depositType === "Returned" && form.depositAmount !== ""
      ? `-${String(form.depositAmount).replace(/^-/, "")}`
      : form.depositAmount;

  // Fetch goods on mount
  useEffect(() => {
    const fetchGoods = async () => {
      try {
        const response = await getAllGoods();
        console.log("DeliveredChallan - Goods API response:", response);
        setGoods(response.data?.data || response.data || []);
      } catch (error) {
        console.error("DeliveredChallan - Error fetching goods:", error);
        toast.error("Failed to load products");
      }
    };
    fetchGoods();
  }, []);

  // Fetch next challan number on mount (only for new challans)
  useEffect(() => {
    const fetchNextChallanNumber = async () => {
      if (id) return; // Don't fetch if editing existing challan

      try {
        // If manual challan is enabled, show only the sequence number
        if (settings.useManualChallan) {
          const manualNumber = settings.manualChallanStart;
          console.log("Manual challan sequence:", manualNumber);
          setForm(prev => ({
            ...prev,
            challanSequence: manualNumber.toString(),
            challanNumber: ""
          }));
        } else {
          // If manual challan is disabled, generate full challan number from settings
          const prefix = settings.invoicePrefix;
          const format = settings.challanNumberFormat;
          const sequence = settings.challanSequence;
          const challanNumber = `${prefix}/D/${format}/${sequence}`;
          console.log("Auto challan number:", challanNumber);
          setForm(prev => ({
            ...prev,
            challanNumber: challanNumber,
            challanSequence: ""
          }));
        }
      } catch (error) {
        console.error("Error fetching next challan number:", error);
      }
    };
    fetchNextChallanNumber();
  }, [id, settings]);

  // Auto-increment manual challan sequence after successful save
  useEffect(() => {
    if (settings.useManualChallan && !id && form.challanSequence && !saving && lastSavedSequence !== null) {
      const currentSequence = parseInt(form.challanSequence, 10);
      if (!isNaN(currentSequence) && currentSequence === lastSavedSequence) {
        const nextSequence = currentSequence + 1;
        console.log("Next manual challan sequence:", nextSequence);
        setForm(prev => ({
          ...prev,
          challanSequence: nextSequence.toString()
        }));
        setLastSavedSequence(null);
      }
    }
  }, [saving, lastSavedSequence, form.challanSequence, id, settings.useManualChallan]);

  // Fetch challan data for editing (same API as ChallanForm)
  useEffect(() => {
    const fetchChallanData = async () => {
      if (!id) return;

      try {
        console.log("Fetching challan data for ID:", id);
        const response = await api.get(`challans/getByChallanId/${id}`);
        console.log("API response:", response);
        const challanData = response.data;
        console.log("Challan data:", challanData);

        // Populate form fields with challan data (same mapping as ChallanForm)
        if (challanData.customerId) {
          setForm(prev => ({
            ...prev,
            customer: challanData.customerName,
            customerId: challanData.customerId
          }));
          setCustomerSearchTerm(challanData.customerName);
        }
        if (challanData.challanNumber) {
          setForm(prev => ({
            ...prev,
            challanNumber: challanData.challanNumber
          }));
        }
        if (challanData.challanType) {
          setForm(prev => ({
            ...prev,
            challanType: challanData.challanType
          }));
        }
        if (challanData.date) {
          setForm(prev => ({
            ...prev,
            date: challanData.date ? challanData.date.split("T")[0] : challanData.date
          }));
        }
        if (challanData.siteLocation) {
          setForm(prev => ({
            ...prev,
            siteLocation: challanData.siteLocation
          }));
        }
        if (challanData.vehicleNumber) {
          setForm(prev => ({
            ...prev,
            vehicleNo: challanData.vehicleNumber
          }));
        }
        if (challanData.transporter) {
          setForm(prev => ({
            ...prev,
            transporter: challanData.transporter
          }));
        }
        if (challanData.driverName) {
          setForm(prev => ({
            ...prev,
            driverDetails: challanData.driverName
          }));
        }
        if (challanData.deliveryDetails) {
          setForm(prev => ({
            ...prev,
            deliveryDetails: challanData.deliveryDetails
          }));
        }
        if (challanData.purchaseOrderNo) {
          setForm(prev => ({
            ...prev,
            poNo: challanData.purchaseOrderNo
          }));
        }
        if (challanData.depositeNarration) {
          setForm(prev => ({
            ...prev,
            depositNarration: challanData.depositeNarration
          }));
        }
        if (challanData.depositeAmount !== undefined) {
          setForm(prev => ({
            ...prev,
            depositAmount: challanData.depositeAmount
          }));
        }

        // Populate products
        if (challanData.items && challanData.items.length > 0) {
          const mappedProducts = challanData.items.map((item, index) => ({
            id: Date.now() + index,
            name: item.name || "",
            type: item.type || "",
            deliveredQty: item.deliveredQty || 0,
            srNo: item.srNo || [],
            selectedSrNos: item.srNo || [],
            cylinderData: [],
          }));
          setProducts(mappedProducts);
          console.log("Mapped products:", mappedProducts);
        }
      } catch (error) {
        console.error("Error fetching challan data:", error);
        toast.error("Failed to load challan data");
      }
    };

    fetchChallanData();
  }, [id]);

  // Fetch customers using searchCustomers API
  useEffect(() => {
    const fetchCustomers = async () => {
      try {
        const response = await searchCustomers({ name: "" });
        console.log("DeliveredChallan - Customers response:", response);
        const customerData = response.data?.data || response.data || [];
        setCustomers(customerData);
        setFilteredCustomers(customerData);
      } catch (error) {
        console.error("DeliveredChallan - Error fetching customers:", error);
        try {
          const response = await searchCustomers({ name: "a" });
          const customerData = response.data?.data || response.data || [];
          setCustomers(customerData);
          setFilteredCustomers(customerData);
        } catch (err) {
          console.error("DeliveredChallan - Fallback error:", err);
          toast.error("Failed to load customers");
        }
      }
    };
    fetchCustomers();
  }, []);

  /* RECEIVE PRODUCTS FROM INSERT PRODUCT PAGE */
  const initializedProducts = React.useRef(false);

  useEffect(() => {
    if (initializedProducts.current) return;

    if (!location.state?.products?.length) return;

    initializedProducts.current = true;

    const mapped = location.state.products.map((p) => ({
      id: Date.now() + Math.random(),
      name: p.product || "",
      type: p.type || "",
      deliveredQty: Number(p.delivered || 0),
      receivedQty: Number(p.received || 0),
      srNo: p.srNo || [],
    }));

    setForm((prev) => ({
      ...prev,
      products: mapped,
    }));

    navigate(location.pathname, {
      replace: true,
      state: {},
    });
  }, [location.state, navigate, location.pathname]);

  /* Pre-fill from Sales Order data when launched from SO → Create Challan */
  const initializedFromSO = React.useRef(false);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (initializedFromSO.current) return;
    const soData = location.state?.salesOrderData;
    if (!soData) return;
    initializedFromSO.current = true;

    setForm(prev => ({
      ...prev,
      customer: soData.customer || '',
      customerId: soData.customerId || '',
      poNo: soData.salesOrderNo || '',
    }));
    setCustomerSearchTerm(soData.customer || '');

    if (soData.customerId) {
      fetchCustomerInventory(soData.customerId);
    } // eslint-disable-next-line react-hooks/exhaustive-deps

    const items = soData.items || [];
    if (items.length > 0) {
      const delivered = Number(soData.deliveredQty || 0);
      const mappedProducts = items.map((it, idx) => {
        const ordered = Number(it.qty || 0);
        const pending = Math.max(ordered - delivered, 0);
        return {
          id: Date.now() + idx,
          name: it.item || '',
          type: '',
          deliveredQty: pending > 0 ? pending : 0,
          receivedQty: 0,
          srNo: [],
          selectedSrNos: [],
          cylinderData: [],
          selectedBatch: '',
          selectedSrno: '',
          goodsItemId: idx + 1,
          _sourceSalesOrder: soData,
          _maxQty: pending,
        };
      });
      setProducts(mappedProducts);
    }

    navigate(location.pathname, { replace: true, state: {} });
  }, [location.state, navigate, location.pathname]);

  /* Scroll to extended form when it opens */
  useEffect(() => {
    if (showExtendedProductForm && extendedFormRef.current) {
      setTimeout(() => {
        extendedFormRef.current.scrollIntoView({
          behavior: 'smooth',
          block: 'start'
        });
      }, 100);
    }
  }, [showExtendedProductForm]);

  function handleChange(e) {
    setForm({ ...form, [e.target.name]: e.target.value });
  }


  function handleProductsAdded(products) {
    const mappedProducts = products.map(p => ({
      name: p.product,
      qty: form.challanType?.includes("Delivered")
        ? Number(p.delivered)
        : Number(p.received),
      goodsItemId: p.goodsItemId,
      type: p.type || "",
      srNo: [],
      selectedSrNos: [],
    }));

    setForm(prev => ({
      ...prev,
      products: [...prev.products, ...mappedProducts]
    }));

    setShowExtendedProductForm(false);
    toast.success(`${products.length} products added successfully!`);
  }


  const handleCustomerSelect = (customer) => {
    setForm(prev => ({
      ...prev,
      customer: customer.name || customer.customerName,
      customerId: customer.id
    }));
    setCustomerSearchTerm(customer.name || customer.customerName);
    setFieldErrors(prev => ({ ...prev, customer: false }));
    setShowCustomerDropdown(false);
    setFilteredCustomers(customers);

    // Fetch customer inventory when customer is selected
    fetchCustomerInventory(customer.id);
  };

  // Fetch customer inventory
  const fetchCustomerInventory = async (customerId) => {
    if (!customerId) return;

    try {
      setInventoryLoading(true);
      const response = await searchCustomers({ name: customerSearchTerm || form.customer || "" });

      if (response.status === 200) {
        const data = response.data?.data || response.data || [];
        console.log("Customer search results:", data);
        setCustomerInventory(Array.isArray(data) ? data : []);
      } else {
        console.error("Failed to fetch customer search results");
        setCustomerInventory([]);
      }
    } catch (error) {
      console.error("Error fetching customer inventory:", error);
      setCustomerInventory([]);
    } finally {
      setInventoryLoading(false);
    }
  };

  async function handleSubmit(e, shouldRedirect = true) {
    e.preventDefault();
    if (saving) return;

    console.log("Form data:", form);
    console.log("Customer search term:", customerSearchTerm);
    console.log("Products:", products);
    console.log("Editing challan ID:", id);

    // Reset field errors
    const newFieldErrors = {
      customer: !form.customerId,
      siteLocation: !form.siteLocation,
      products: products.length === 0,
    };

    setFieldErrors(newFieldErrors);

    // Check for validation errors and build specific error message
    const errorMessages = [];
    if (newFieldErrors.customer) errorMessages.push("Customer");
    if (newFieldErrors.siteLocation) errorMessages.push("Site Location");
    if (newFieldErrors.products) errorMessages.push("Products");

    if (errorMessages.length > 0) {
      console.log("Validation failed - missing fields:", errorMessages);
      toast.error(`Please fill required fields: ${errorMessages.join(', ')}`);
      return;
    }

    const token = TokenManager.getToken();
    if (!token) {
      toast.error("Session expired. Please login again.");
      navigate("/");
      return;
    }

    const payload = {
      customerId: form.customerId,
      customerName: form.customer,
      siteLocation: form.siteLocation,
      vehicleNumber: form.vehicleNo,
      deliveredChallanNo: "",
      driverName: form.driverDetails,
      driverNumber: "",
      transporter: form.transporter,
      lrNumber: "",
      challanNumber: settings.useManualChallan
        ? form.challanSequence
        : (form.challanNumber === "AUTO" ? "" : form.challanNumber),
      challanType: "DELIVERED",
      date: form.date,
      purchaseOrderNo: form.poNo,
      depositType: form.depositType,
      depositeType: form.depositType,
      depositAmount: Number(form.depositAmount) || 0,
      depositeAmount: Number(form.depositAmount) || 0,
      depositInformation: form.depositNarration || "",
      depositeInformation: form.depositNarration || "",
      returnedAmount:
        form.depositType === "Returned" ? -Math.abs(Number(form.depositAmount) || 0) : Number(form.depositAmount) || 0,
      depositeNarration: form.depositNarration,
      deliveryDetails: form.deliveryDetails,

      sourceSalesOrderId: products[0]?._sourceSalesOrder?.id || null,
      sourceSalesOrderNumber: products[0]?._sourceSalesOrder?.salesOrderNo || null,

      items: products
        .filter((p) => p.deliveredQty && p.deliveredQty > 0)
        .map((p, idx) => {
          const srNos = Array.isArray(p.selectedSrNos) ? p.selectedSrNos : [];
          const batchNos = p.cylinderData?.length
            ? [...new Set(p.cylinderData.filter(c => srNos.includes(c.srno)).map(c => c.batchNo))]
            : (p.selectedBatch ? [p.selectedBatch] : []);

          return {
            goodsItemId: p.goodsItemId || idx + 1,
            name: p.name,
            type: p.type || "",
            deliveredQty: Number(p.deliveredQty),
            receivedQty: 0,
            srNo: srNos,
            batchNo: batchNos,
            rate: Number(p._sourceSalesOrder?.items?.find(si => (si.item || '').toLowerCase() === p.name.toLowerCase())?.rate || 0),
          };
        }),
    };

    const srCountError = validateChallanItemSrCounts(payload.items);
    if (srCountError) {
      toast.error(srCountError);
      return;
    }

    try {
      setSaving(true);
      let response;
      if (id) {
        // Update existing challan
        response = await api.put(`challans/updateChallanById/${id}`, payload);
      } else {
        // Create new challan
        response = await api.post("challans/create", payload);
      }
      
      if (response.status === 200 || response.status === 201) {
        // Update Sales Order deliveredQty if this challan was created from a SO
        const soRef = products[0]?._sourceSalesOrder;
        if (soRef?.id) {
          try {
            const soList = JSON.parse(localStorage.getItem('salesOrders') || '[]');
            const totalDeliveredInThisChallan = payload.items.reduce((s, it) => s + Number(it.deliveredQty || 0), 0);
            const updatedSOs = soList.map(so => {
              if (String(so.id) === String(soRef.id)) {
                const newDelivered = Number(so.deliveredQty || 0) + totalDeliveredInThisChallan;
                const totalOrdered = (so.items || []).reduce((s, it) => s + Number(it.qty || 0), 0);
                const pending = Math.max(totalOrdered - newDelivered, 0);
                let newStatus = so.status;
                if (pending <= 0 && totalOrdered > 0) newStatus = 'Delivered';
                else if (newDelivered > 0) newStatus = 'Partially Delivered';
                return { ...so, deliveredQty: newDelivered, pendingQty: pending, status: newStatus, updatedAt: new Date().toISOString() };
              }
              return so;
            });
            localStorage.setItem('salesOrders', JSON.stringify(updatedSOs));
          } catch (e) { console.error('Failed to update SO deliveredQty:', e); }
        }

        if (shouldRedirect) {
          // Show toast with 3 second auto-close, navigate after toast closes
          const message = id ? "Delivered Challan updated successfully!" : "Delivered Challan created successfully!";
          toast.success(message, {
            autoClose: 3000,
            onClose: () => {
              setShowInvoicePopup(false);
              navigate("/dashboard/challans/view");
            }
          });
        } else {
          const message = id ? "Delivered Challan updated successfully!" : "Delivered Challan created successfully!";
          toast.success(message);
          // Stay on same page
          if (settings.useManualChallan && !id) {
            // Set last saved sequence for auto-increment
            setLastSavedSequence(parseInt(form.challanSequence, 10));
          } else {
            handleReset();
          }
          if (form.customerId) {
            await fetchCustomerInventory(form.customerId);
          }
          window.dispatchEvent(new CustomEvent('refreshDashboard'));
        }
      } else {
        toast.error("Unexpected response status: " + response.status);
      }
    } catch (err) {
      console.error("Error Response:", err.response?.data);
      
      const errorMessage = getErrorMessage(err);
      toast.error(errorMessage);
      
      // Handle authentication errors separately
      if (err.response?.status === 401) {
        navigate("/");
      }
    } finally {
      setSaving(false);
    }
  }

  function handleReset() {
    setForm({
      challanNumber: "",
      challanSequence: "",
      customer: "",
      customerId: "",
      challanType: "Delivered Challan",
      date: new Date().toISOString().split("T")[0],
      siteLocation: "",
      vehicleNo: "",
      transporter: "",
      driverDetails: "",
      driverNumber: "",
      deliveryDetails: "",
      poNo: "",
      depositType: "Deposit",
      depositAmount: "",
      depositNarration: "",
      products: [],
    });
    setCustomerSearchTerm("");
    setShowCustomerDropdown(false);
    setShowDepositDropdown(false);
    setCustomers([]);
    setProducts([{ id: Date.now(), name: "", type: "", deliveredQty: "", receivedQty: "", srNo: [] }]);
    // Clear field errors
    setFieldErrors({
      customer: false,
      siteLocation: false,
      products: false,
    });
  }

  // Additional functions for new product management
  const addProductRow = () => {
    // Prevent adding if last row is empty
    if (products.length && products[products.length - 1].name === "") {
      return;
    }

    const newProduct = {
      id: Date.now(),
      name: "",
      type: "",
      deliveredQty: "",
      receivedQty: "",
      srNo: [],        // must be array, not string
      selectedSrNos: [],
      cylinderData: [],
      selectedBatch: "",
      selectedSrno: ""
    };
    setProducts([...products, newProduct]);
  };

  const removeProductRow = (index) => {
    setProducts(products.filter((_, i) => i !== index));
  };

  const handleProductChange = (index, field, value) => {
    setProducts(prev =>
      prev.map((item, i) =>
        i === index ? { ...item, [field]: value } : item
      )
    );
  };

  return (
    <div className="dc-container">
      <div className="dc-header">
        <div className="dc-header-icon">
          <FiTruck size={20} />
        </div>
        <div>
          <h2>Delivered Challan Entry</h2>
          <p>Create a new delivery challan for customer orders</p>
        </div>
      </div>

      <form onSubmit={handleSubmit}>
          <div className="dc-grid">
            {/* === CUSTOMER & LOCATION === */}
            <div className="dc-section">
              <div className="dc-section-title">
                <FiUser size={16} />
                Customer &amp; Location
              </div>
              <div className="dc-grid gap-20">
                <div className="dc-field full">
                  <label className="dc-field-label">
                    <FiUser size={14} />
                    <span>Customer Name *</span>
                  </label>
                  <div className="customer-search-container">
                    <div className="customer-search-input-wrapper">
                      <input
                        name="customer"
                        value={customerSearchTerm}
                        onChange={(e) => {
                          const searchTerm = e.target.value;
                          setCustomerSearchTerm(searchTerm);
                          // Typing changes the search value, so require a fresh
                          // customer selection from the dropdown.
                          setForm(prev => ({
                            ...prev,
                            customer: searchTerm,
                            customerId: ""
                          }));
                          // Filter customers based on search term
                          if (searchTerm.length >= 0) {
                            const filtered = customers.filter(customer =>
                              (customer.name || customer.customerName || "").toLowerCase().includes(e.target.value.toLowerCase())
                            );
                            setFilteredCustomers(filtered);
                            setShowCustomerDropdown(true);
                          }
                          // Clear error when user starts typing
                          if (fieldErrors.customer) {
                            setFieldErrors(prev => ({ ...prev, customer: false }));
                          }
                        }}
                        onFocus={() => setShowCustomerDropdown(true)}
                        onBlur={() => setTimeout(() => setShowCustomerDropdown(false), 200)}
                        placeholder="Search customer..."
                        required
                        className={`customer-search-input ${fieldErrors.customer ? 'has-error' : ''}`}
                      />
                      <div className="customer-search-icon">
                        {showCustomerDropdown ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                      </div>
                    </div>
                    {fieldErrors.customer && (
                      <div className="dc-field-error customer-field-error">
                        Please select a customer from the dropdown
                      </div>
                    )}
                    {showCustomerDropdown && (
                      <div className="customer-dropdown" style={{ position: 'absolute', zIndex: 99999 }}>
                        {filteredCustomers.length === 0 ? (
                          <div className="customer-no-results">
                            <div className="no-results-icon"><FiSearch size={20} /></div>
                            <div className="no-results-text">No customers found</div>
                            <div className="no-results-hint">Try searching with different keywords</div>
                          </div>
                        ) : (
                          filteredCustomers.map((customer) => (
                            <div
                              key={customer.id}
                              className="customer-option"
                              onMouseDown={(event) => {
                                event.preventDefault();
                                handleCustomerSelect(customer);
                              }}
                            >
                              <div className="customer-option-main">
                                <div className="customer-name">
                                  {customer.name || customer.customerName}
                                </div>
                                {customer.email && (
                                  <div className="customer-email">
                                    <FiMail size={12} /> {customer.email}
                                  </div>
                                )}
                                {customer.phone && (
                                  <div className="customer-phone">
                                    <FiPhone size={12} /> {customer.phone}
                                  </div>
                                )}
                              </div>
                              <div className="customer-select-icon"><FiCheck size={14} /></div>
                            </div>
                          ))
                        )}
                      </div>
                    )}
                  </div>
                </div>

                <div className="dc-field full">
                  <label className="dc-field-label">
                    <FiMapPin size={14} />
                    <span>Site Location *</span>
                  </label>
                  <div className="customer-search-input-wrapper location-input">
                    <FiMapPin size={15} className="dc-input-icon" />
                    <input
                      name="siteLocation"
                      value={form.siteLocation}
                      onChange={(e) => {
                        handleChange(e);
                        // Clear error when user starts typing
                        if (fieldErrors.siteLocation) {
                          setFieldErrors(prev => ({ ...prev, siteLocation: false }));
                        }
                      }}
                      placeholder="Enter site location"
                      required
                      className={`customer-search-input ${fieldErrors.siteLocation ? 'has-error' : ''}`}
                    />
                  </div>
                  {fieldErrors.siteLocation && (
                    <div className="dc-field-error">
                      Site location is required
                    </div>
                  )}
                </div>

                {/* Geo Location Section */}
                {settings.enableChallanGeoLocation && (
                  <div className="dc-field full">
                    <label className="dc-field-label">
                      <FiMap size={14} />
                      <span>Geo Location</span>
                    </label>
                    <div className="geo-location-wrapper">
                      <button
                        type="button"
                        onClick={handleFetchGeoLocation}
                        disabled={fetchingLocation}
                        className="geo-location-button"
                      >
                        {fetchingLocation ? (
                          <>
                            <span className="loading-spinner"></span>
                            Capturing Location...
                          </>
                        ) : (
                          <>
                            <FiMap size={16} />
                            Capture Current Location
                          </>
                        )}
                      </button>
                      {geoLocation && (
                        <div className="geo-location-info">
                          <div className="geo-location-item">
                            <span className="geo-label">Latitude:</span>
                            <span className="geo-value">{geoLocation.latitude.toFixed(6)}</span>
                          </div>
                          <div className="geo-location-item">
                            <span className="geo-label">Longitude:</span>
                            <span className="geo-value">{geoLocation.longitude.toFixed(6)}</span>
                          </div>
                          <div className="geo-location-item">
                            <span className="geo-label">Accuracy:</span>
                            <span className="geo-value">{geoLocation.accuracy.toFixed(0)}m</span>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                )}

                {/* Image Upload Section */}
                {settings.enableChallanImageUpload && (
                  <div className="dc-field full">
                    <label className="dc-field-label">
                      <FiCamera size={14} />
                      <span>Challan Images</span>
                    </label>
                    <div className="image-upload-wrapper">
                      <input
                        type="file"
                        id="challan-image-upload"
                        multiple
                        accept="image/*"
                        onChange={handleImageUpload}
                        className="image-upload-input"
                      />
                      <label htmlFor="challan-image-upload" className="image-upload-button">
                        <FiCamera size={16} />
                        Upload Images
                      </label>
                      {imagePreviews.length > 0 && (
                        <div className="image-previews">
                          {imagePreviews.map((preview, index) => (
                            <div key={index} className="image-preview-item">
                              <img src={preview} alt={`Preview ${index + 1}`} />
                              <button
                                type="button"
                                onClick={() => handleRemoveImage(index)}
                                className="image-remove-button"
                              >
                                <FiX size={14} />
                              </button>
                            </div>
                          ))}
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* === BASIC INFO === */}
            <div className="dc-section">
              <div className="dc-section-title">
                <FiCalendar size={16} />
                Basic Information
              </div>
              <div className="dc-flex-column">
                <div className="dc-field full">
                  <label className="dc-field-label">
                    <FiCalendar size={14} />
                    <span>Date *</span>
                  </label>
                  <div className="customer-search-input-wrapper date-input">
                    <input
                      name="date"
                      type="date"
                      value={form.date}
                      onChange={handleChange}
                      required
                      className="customer-search-input"
                    />
                  </div>
                </div>

                <div className="dc-grid two-columns gap-20" style={{ padding: 0 }}>
                  <div className="dc-info-field">
                    <label className="dc-field-label">
                      <FiFileText size={14} />
                      <span>Challan Type</span>
                    </label>
                    <div className="dc-info-value delivered">
                      {form.challanType}
                    </div>
                  </div>

                  <div className="dc-info-field">
                    <label className="dc-field-label">
                      <FiHash size={14} />
                      <span>Challan Number</span>
                    </label>
                    <div className="dc-info-value number">
                      {settings.useManualChallan ? form.challanSequence : form.challanNumber}
                    </div>
                  </div>
                </div>
              </div>
            </div>

            {/* === OPTIONAL SECTIONS TABS === */}
            <div className="dc-tabs-section">
              <div className="dc-tabs-header">
                <button
                  type="button"
                  className={`dc-tab ${activeSection === 'transport' ? 'active' : ''}`}
                  onClick={() => setActiveSection(activeSection === 'transport' ? null : 'transport')}
                >
                  <FiTruck size={16} />
                  <span>Transport Details</span>
                </button>
                <button
                  type="button"
                  className={`dc-tab ${activeSection === 'delivery' ? 'active' : ''}`}
                  onClick={() => setActiveSection(activeSection === 'delivery' ? null : 'delivery')}
                >
                  <FiPackage size={16} />
                  <span>Delivery Information</span>
                </button>
                <button
                  type="button"
                  className={`dc-tab ${activeSection === 'deposit' ? 'active' : ''}`}
                  onClick={() => setActiveSection(activeSection === 'deposit' ? null : 'deposit')}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="currentColor" className="bi bi-currency-rupee" viewBox="0 0 16 16">
                    <path d="M4 3.06h2.726c1.22 0 2.12.575 2.325 1.724H4v1.051h5.051C8.855 7.001 8 7.558 6.788 7.558H4v1.317L8.437 14h2.11L6.095 8.884h.855c2.316-.018 3.465-1.476 3.688-3.049H12V4.784h-1.345c-.08-.778-.357-1.335-.793-1.732H12V2H4z"/>
                  </svg>
                  <span>Deposit Information</span>
                </button>
              </div>

              {/* TRANSPORT DETAILS CONTENT */}
              {activeSection === 'transport' && (
                <div className="dc-tab-content">
                  <div className="dc-grid two-columns gap-20">
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiTruck size={14} />
                        <span>Vehicle Number</span>
                      </label>
                      <input
                        name="vehicleNo"
                        value={form.vehicleNo}
                        onChange={handleChange}
                        placeholder="Enter vehicle number"
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiTruck size={14} />
                        <span>Transporter</span>
                      </label>
                      <input
                        name="transporter"
                        value={form.transporter}
                        onChange={handleChange}
                        placeholder="Enter transporter details"
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiUser size={14} />
                        <span>Driver Name</span>
                      </label>
                      <input
                        name="driverDetails"
                        value={form.driverDetails}
                        onChange={handleChange}
                        placeholder="Enter driver name"
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiPhone size={14} />
                        <span>Driver Number</span>
                      </label>
                      <input
                        name="driverNumber"
                        value={form.driverNumber}
                        onChange={handleChange}
                        placeholder="Enter driver number"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* DELIVERY INFORMATION CONTENT */}
              {activeSection === 'delivery' && (
                <div className="dc-tab-content">
                  <div className="dc-grid two-columns gap-20">
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiPackage size={14} />
                        <span>Delivery Details</span>
                      </label>
                      <input
                        name="deliveryDetails"
                        value={form.deliveryDetails}
                        onChange={handleChange}
                        placeholder="Enter delivery details"
                      />
                    </div>

                    <div className="dc-field">
                      <label className="dc-field-label">
                        <FiFileText size={14} />
                        <span>PO No.</span>
                      </label>
                      <input
                        name="poNo"
                        value={form.poNo}
                        onChange={handleChange}
                        placeholder="Enter PO number"
                      />
                    </div>
                  </div>
                </div>
              )}

              {/* DEPOSIT INFORMATION CONTENT */}
              {activeSection === 'deposit' && (
                <div className="dc-tab-content">
                  <div className="dc-grid two-columns gap-20">
                    <div className="dc-field">
                      <label className="dc-field-label">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-currency-rupee" viewBox="0 0 16 16">
                          <path d="M4 3.06h2.726c1.22 0 2.12.575 2.325 1.724H4v1.051h5.051C8.855 7.001 8 7.558 6.788 7.558H4v1.317L8.437 14h2.11L6.095 8.884h.855c2.316-.018 3.465-1.476 3.688-3.049H12V4.784h-1.345c-.08-.778-.357-1.335-.793-1.732H12V2H4z"/>
                        </svg>
                        <span>Deposit Type</span>
                      </label>
                      <div className="deposit-dropdown">
                        <div
                          className="customer-search-input-wrapper"
                          onClick={() => setShowDepositDropdown(!showDepositDropdown)}
                        >
                          <input
                            readOnly
                            value={form.depositType || "Deposit"}
                            className="customer-search-input"
                            onBlur={() => setTimeout(() => setShowDepositDropdown(false), 200)}
                          />
                          <div className="customer-search-icon">
                            {showDepositDropdown ? <FiChevronUp size={16} /> : <FiChevronDown size={16} />}
                          </div>
                        </div>
                        {showDepositDropdown && (
                          <div className="customer-dropdown">
                            {["Deposit", "Returned"].map((option) => (
                              <div
                                key={option}
                                className={`customer-option ${form.depositType === option ? 'selected' : ''}`}
                                onMouseDown={() => {
                                  setForm({
                                    ...form,
                                    depositType: option
                                  });
                                  setShowDepositDropdown(false);
                                }}
                              >
                                <div className="customer-option-main">
                                  <div className="customer-name">
                                    {option}
                                  </div>
                                </div>
                                {form.depositType === option && (
                                  <div className="customer-select-icon">
                                    <FiCheck size={14} />
                                  </div>
                                )}
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="dc-field">
                      <label className="dc-field-label">
                        <svg xmlns="http://www.w3.org/2000/svg" width="14" height="14" fill="currentColor" className="bi bi-currency-rupee" viewBox="0 0 16 16">
                          <path d="M4 3.06h2.726c1.22 0 2.12.575 2.325 1.724H4v1.051h5.051C8.855 7.001 8 7.558 6.788 7.558H4v1.317L8.437 14h2.11L6.095 8.884h.855c2.316-.018 3.465-1.476 3.688-3.049H12V4.784h-1.345c-.08-.778-.357-1.335-.793-1.732H12V2H4z"/>
                        </svg>
                        <span>{form.depositType === 'Returned' ? 'Returned Amount ()' : 'Deposit Amount ()'}</span>
                      </label>
                      <div className="customer-search-input-wrapper">
                        <input
                          name="depositAmount"
                          type="text"
                          value={displayDepositAmount}
                          onChange={(e) => {
                            const nextValue = e.target.value.replace(/^-/, "");
                            setForm(prev => ({ ...prev, depositAmount: nextValue }));
                          }}
                          placeholder="0.00"
                          className="customer-search-input"
                        />
                      </div>
                    </div>

                    <div className="dc-field full">
                      <label className="dc-field-label center">
                        <FiMessageSquare size={14} />
                        <span>Deposit Narration</span>
                      </label>
                      <div className="customer-search-input-wrapper">
                        <input
                          name="depositNarration"
                          value={form.depositNarration}
                          onChange={handleChange}
                          placeholder="Enter narration"
                          className="customer-search-input"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

            </div>
          </div>

          <div style={{ marginTop: '16px', overflow: 'visible', width: '100%', maxWidth: '100%', boxSizing: 'border-box' }}>
            {fieldErrors.products && (
              <div className="dc-field-error" style={{ marginBottom: '8px' }}>
                At least one product is required
              </div>
            )}
            <ChallanProductsSection
              count={products.length}
              hasError={fieldErrors.products}
              subtitle="Select products, delivered quantities, batches, and optional SR numbers."
              onAddProduct={() => {
                addProductRow();
                if (fieldErrors.products) {
                  setFieldErrors(prev => ({ ...prev, products: false }));
                }
              }}
              actions={
                <>
                  <button
                    type="button"
                    className="challan-reset-btn"
                    onClick={handleReset}
                    disabled={saving}
                  >
                    Reset
                  </button>
                  <button
                    type="button"
                    className="challan-create-btn"
                    onClick={(e) => handleSubmit(e, false)}
                    disabled={saving}
                  >
                    {saving ? <span className="challan-create-spinner" /> : <FaCheckCircle size={15} />}
                    {saving ? "Saving..." : "Save & Continue"}
                  </button>
                  <button
                    type="button"
                    className="challan-create-btn"
                    onClick={(e) => {
                      e.preventDefault();
                      
                      // Validate form before showing popup
                      const newFieldErrors = {
                        customer: !form.customerId,
                        siteLocation: !form.siteLocation,
                        products: products.length === 0,
                      };

                      setFieldErrors(newFieldErrors);

                      const errorMessages = [];
                      if (newFieldErrors.customer) errorMessages.push("Customer");
                      if (newFieldErrors.siteLocation) errorMessages.push("Site Location");
                      if (newFieldErrors.products) errorMessages.push("Products");

                      if (errorMessages.length > 0) {
                        toast.error(`Please fill the form first: ${errorMessages.join(', ')}`);
                        return;
                      }

                      // Check if invoice popup should be shown after challan creation
                      if (settings.showInvoicePopupAfterChallan) {
                        setShowInvoicePopup(true);
                      } else {
                        // Directly create challan without popup
                        handleSubmit({ preventDefault: () => {} }, true);
                      }
                    }}
                    disabled={saving}
                  >
                    {saving ? <span className="challan-create-spinner" /> : <FaCheckCircle size={15} />}
                    {saving ? "Creating..." : "Generate Challan"}
                  </button>
                </>
              }
            >
                <ProductTable
                  prefix="dc"
                  pageType={pageType}
                  products={products}
                  allProducts={allProducts}
                  customerInventory={customerInventory}
                  inventoryLoading={inventoryLoading}
                  goods={goods}
                  onUpdate={handleProductChange}
                  onRemove={removeProductRow}
                />
            </ChallanProductsSection>
          </div>
        </form>

      {/* Extended Product Form - Inline */}
      {showExtendedProductForm && (
        <div className="dc-extended-product-section" ref={extendedFormRef}>
          <div className="dc-extended-product-header">
            <h3><FaBox /> Add Multiple Products</h3>
            <button
              className="dc-extended-close-btn"
              onClick={() => setShowExtendedProductForm(false)}
            >
              <FiX size={18} />
            </button>
          </div>
          <div className="dc-extended-product-content">
            <InsertProduct
              onProductsAdded={handleProductsAdded}
              onClose={() => setShowExtendedProductForm(false)}
              isInModal={false}
            />
          </div>
        </div>
      )}

      <ChallanInvoicePopup
        open={showInvoicePopup}
        type={form.challanType || "Challan"}
        form={form}
        products={products || []}
        saving={saving}
        onClose={() => setShowInvoicePopup(false)}
        onGenerate={() => {
          console.log('DeliveredChallan - products before submit:', products);
          setShowInvoicePopup(false);
          handleSubmit({ preventDefault: () => {} }, true);
        }}
      />
    </div>
  );
}

export default DeliveredChallan;
